import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  TextField,
  IconButton
} from '@mui/material';
import { Add as AddIcon, PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import { walletAPI } from '../services/api';
import { CreditCard as CardsIcon } from '@mui/icons-material';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ py: 2 }}>{children}</Box> : null;
}

const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const formatCurrency = (n) => n != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) : '—';

export default function PolicyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [memberForm, setMemberForm] = useState({ first_name: '', last_name: '', date_of_birth: '', relation_to_primary: '', photo_url: '' });
  const [memberFormLoading, setMemberFormLoading] = useState(false);
  const [memberFormError, setMemberFormError] = useState(null);

  const fetchPolicy = async () => {
    if (!id) return;
    try {
      const data = await walletAPI.getPolicy(id);
      setPolicy(data?.data ?? data);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load policy');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchPolicy();
  }, [id]);

  if (loading) return <Container sx={{ py: 3 }}><Typography>Loading…</Typography></Container>;
  if (error || !policy) return <Container sx={{ py: 3 }}><Typography color="error">{error || 'Policy not found'}</Typography><Button onClick={() => navigate('/wallet')}>Back</Button></Container>;

  const limits = policy.limits || [];
  const documents = policy.documents || [];
  const members = policy.members || [];
  const activities = policy.activities || [];

  const handleMemberPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMemberFormError(null);
    setMemberFormLoading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const url = await walletAPI.uploadMemberPhoto(formData);
      setMemberForm((f) => ({ ...f, photo_url: url }));
    } catch (err) {
      setMemberFormError(err.response?.data?.error || err.message || 'Photo upload failed');
    } finally {
      setMemberFormLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!memberForm.first_name?.trim() || !memberForm.last_name?.trim()) {
      setMemberFormError('First name and last name required');
      return;
    }
    setMemberFormError(null);
    setMemberFormLoading(true);
    try {
      await walletAPI.addMember(id, {
        first_name: memberForm.first_name.trim(),
        last_name: memberForm.last_name.trim(),
        date_of_birth: memberForm.date_of_birth || null,
        relation_to_primary: memberForm.relation_to_primary?.trim() || null,
        photo_url: memberForm.photo_url || null
      });
      setMemberForm({ first_name: '', last_name: '', date_of_birth: '', relation_to_primary: '', photo_url: '' });
      setShowAddMember(false);
      await fetchPolicy();
    } catch (err) {
      setMemberFormError(err.response?.data?.message || err.message || 'Failed to add member');
    } finally {
      setMemberFormLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight="bold">{policy.policy_name || policy.policy_type}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => navigate(`/claims/policy/${id}`)}>View claims</Button>
          <Button startIcon={<CardsIcon />} onClick={() => navigate('/cards')}>Digital Card</Button>
        </Box>
      </Box>
      <Typography color="textSecondary">{policy.insurer_name} · {policy.policy_number}</Typography>
      <Typography variant="body2">Valid: {formatDate(policy.start_date)} – {formatDate(policy.end_date)} · Premium: {formatCurrency(policy.premium_amount)}</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2 }}>
        <Tab label="Overview" />
        <Tab label="Coverage" />
        <Tab label="Documents" />
        <Tab label="Family" />
        <Tab label="Activity" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Card><CardContent>
          <Typography variant="subtitle2">Policy type</Typography>
          <Typography>{policy.policy_type}</Typography>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Premium</Typography>
          <Typography>{formatCurrency(policy.premium_amount)} {policy.premium_frequency || 'annual'}</Typography>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Source</Typography>
          <Typography>{policy.source || 'manual'}</Typography>
        </CardContent></Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {limits.length === 0 ? (
          <Typography color="textSecondary">No coverage limits recorded.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Benefit</TableCell>
                  <TableCell align="right">Used</TableCell>
                  <TableCell align="right">Limit</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell>Usage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {limits.map((row) => {
                  const limit = Number(row.annual_limit) || 0;
                  const used = Number(row.amount_used) || 0;
                  const remaining = Number(row.amount_remaining) ?? (limit - used);
                  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.benefit_type}</TableCell>
                      <TableCell align="right">{formatCurrency(used)}</TableCell>
                      <TableCell align="right">{formatCurrency(limit)}</TableCell>
                      <TableCell align="right">{formatCurrency(remaining)}</TableCell>
                      <TableCell>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1 }} color={pct >= 80 ? 'error' : pct >= 50 ? 'warning' : 'primary'} />
                        <Typography variant="caption">{pct.toFixed(0)}%</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Button sx={{ mt: 2 }} onClick={() => navigate('/wallet/limits')}>View all limits</Button>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {documents.length === 0 ? (
          <Typography color="textSecondary">No documents.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Uploaded</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>{doc.document_type}</TableCell>
                    <TableCell>{doc.document_name}</TableCell>
                    <TableCell>{formatDate(doc.upload_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tab} index={3}>
        {memberFormError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{memberFormError}</Typography>}
        {!showAddMember ? (
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setShowAddMember(true)} sx={{ mb: 2 }}>Add member</Button>
        ) : (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>New family member</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <TextField size="small" label="First name" value={memberForm.first_name} onChange={(e) => setMemberForm((f) => ({ ...f, first_name: e.target.value }))} required />
              <TextField size="small" label="Last name" value={memberForm.last_name} onChange={(e) => setMemberForm((f) => ({ ...f, last_name: e.target.value }))} required />
              <TextField size="small" type="date" label="DOB" value={memberForm.date_of_birth} onChange={(e) => setMemberForm((f) => ({ ...f, date_of_birth: e.target.value }))} InputLabelProps={{ shrink: true }} />
              <TextField size="small" label="Relation" placeholder="e.g. Spouse, Child" value={memberForm.relation_to_primary} onChange={(e) => setMemberForm((f) => ({ ...f, relation_to_primary: e.target.value }))} />
              <Box>
                <input accept="image/*" type="file" id="member-photo" style={{ display: 'none' }} onChange={handleMemberPhotoChange} disabled={memberFormLoading} />
                <label htmlFor="member-photo">
                  <IconButton component="span" color="primary" disabled={memberFormLoading} title="Upload photo">
                    <PhotoCameraIcon />
                  </IconButton>
                </label>
                {memberForm.photo_url && <Typography variant="caption" display="block">Photo added</Typography>}
              </Box>
              <Button variant="contained" onClick={handleAddMember} disabled={memberFormLoading}>Save</Button>
              <Button onClick={() => { setShowAddMember(false); setMemberForm({ first_name: '', last_name: '', date_of_birth: '', relation_to_primary: '', photo_url: '' }); setMemberFormError(null); }}>Cancel</Button>
            </Box>
          </Paper>
        )}
        {members.length === 0 && !showAddMember ? (
          <Typography color="textSecondary">No family members.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Photo</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>Relation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      {m.photo_url ? (
                        <Box component="img" src={m.photo_url} alt="" sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover' }} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{m.first_name} {m.last_name}</TableCell>
                    <TableCell>{m.member_type}</TableCell>
                    <TableCell>{formatDate(m.date_of_birth)}</TableCell>
                    <TableCell>{m.relation_to_primary || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tab} index={4}>
        {activities.length === 0 ? (
          <Typography color="textSecondary">No activity.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {activities.map((a) => (
              <Paper key={a.id} sx={{ p: 1.5 }}>
                <Typography variant="body2"><strong>{a.activity_type}</strong> · {a.activity_description}</Typography>
                <Typography variant="caption" color="textSecondary">{formatDate(a.created_at)}</Typography>
              </Paper>
            ))}
          </Box>
        )}
      </TabPanel>

      <Button sx={{ mt: 2 }} onClick={() => navigate('/wallet')}>Back to Wallet</Button>
    </Container>
  );
}
